/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import RichTextParserPropTypes from './RichTextParserPropTypes';
import ContentModule from '../ContentModule';
import Asset from '../Asset';
import ElementLink from '../ElementLink';
import Personalization from '../Personalization'; // Direct import to avoid circular dependency

function RichTextParser({ document, _contentTypeId }) {
  // Get personalization data from Redux store (with fallback for SSR)
  const personalization = useSelector((state) => state.personalization || {});
  const {
    accountType = null,
    productType = [],
    loaded = false
  } = personalization;

  /**
   * Check if personalized content should be rendered based on user attributes
   * @param {Object} target - The content target from Contentful
   * @returns {boolean} - Whether to render this content
   */
  const shouldRenderPersonalizedContent = (target) => {
    // Safety check: if target is null/undefined, don't render
    if (!target || !target._contentTypeId) {
      return false;
    }

    // If it's not a personalization module, always render
    if (target._contentTypeId !== 'testPersionalization') {
      return true;
    }

    // If personalization data hasn't loaded yet, don't render personalized content
    if (!loaded) {
      return false;
    }

    // Get targeting fields from the content module
    const targetAccountTypes = target.targetAccountType || target.accountType || [];
    const targetProductTypes = target.targetProductType || target.productType || [];

    // Normalize to arrays
    const targetAccountTypesArray = Array.isArray(targetAccountTypes) ? targetAccountTypes : [targetAccountTypes];
    const targetProductTypesArray = Array.isArray(targetProductTypes) ? targetProductTypes : [targetProductTypes];

    // BOTH accountType AND productType must be specified
    // If either is missing, don't show content
    if (!targetAccountTypesArray.length || !targetProductTypesArray.length) {
      return false;
    }

    // If user is not logged in (no personalization data), hide content
    if (!accountType || !productType || productType.length === 0) {
      return false;
    }

    // Normalize user data to lowercase for case-insensitive comparison
    const userAccountTypeLower = (accountType || '').toLowerCase();
    const userProductTypesLower = Array.isArray(productType)
      ? productType.map(type => (type || '').toLowerCase())
      : [(productType || '').toLowerCase()];

    // Check account type match (case-insensitive, at least one must match)
    const accountTypeMatches = targetAccountTypesArray.some(targetType =>
      targetType && typeof targetType === 'string' && targetType.toLowerCase() === userAccountTypeLower
    );

    // Check product type match (at least one must match, case-insensitive)
    const productTypeMatches = targetProductTypesArray.some(targetType =>
      targetType && typeof targetType === 'string' && userProductTypesLower.some(userType =>
        userType && typeof userType === 'string' && targetType.toLowerCase() === userType
      )
    );

    return accountTypeMatches && productTypeMatches;
  };
  const options = {
    renderText: (text) =>
      text.split('\n').reduce((children, textSegment, idx) => [...children, idx > 0 && <br />, textSegment], []),
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const target = get(node, 'data.target');

        if (!target || !target._contentTypeId) return null;

        return (
          <section className="section">
            <Asset {...target} />
          </section>
        );
      },
      [BLOCKS.INLINE_ASSET]: (node) => {
        const target = get(node, 'data.target');

        if (!target || !target._contentTypeId) return null;

        return (
          <section className="sub-section">
            <Asset {...target} />
          </section>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {

        const target = get(node, 'data.target');

        if (!target || !target._contentTypeId) return null;

        // Check personalization filtering
        if (!shouldRenderPersonalizedContent(target)) {
          return null; // Don't render if doesn't match user's attributes
        }

        // Handle personalization content directly to avoid circular dependency
        if (target._contentTypeId === 'testPersionalization') {
          return (
            <section className="section">
              <Personalization {...target} />
            </section>
          );
        }

        if (target._contentTypeId === 'elementLink' && target.destinationType === 'Set Anchor Tag') {
          // Manual url only accepts http urls
          return <span id={target?.linkText?.toLowerCase()?.replace(/ /g, '-')} />;
        }

        return (
          <section className="section">
            <ContentModule contentTypeId={target._contentTypeId} fields={target} parent_contentTypeId={_contentTypeId} />
          </section>
        );
      },
      [BLOCKS.INLINE_ENTRY]: (node) => {
        const target = get(node, 'data.target');

        if (!target || !target._contentTypeId) return null;

        // Check personalization filtering
        if (!shouldRenderPersonalizedContent(target)) {
          return null; // Don't render if doesn't match user's attributes
        }

        // Handle personalization content directly to avoid circular dependency
        if (target._contentTypeId === 'testPersionalization') {
          return (
            <section className="sub-section">
              <Personalization {...target} />
            </section>
          );
        }

        return (
          <section className="sub-section">
            <ContentModule contentTypeId={target._contentTypeId} fields={target} />
          </section>
        );
      },
      [BLOCKS.QUOTE]: (node, children) => {
        return <blockquote className="custom-blockquote">{children}</blockquote>

      },
      [INLINES.ENTRY_HYPERLINK]: (node) => {

        const target = get(node, 'data.target');
        const content = get(node, 'content[0].value');

        if (!content || !target || !target._contentTypeId) return null;

        const { _id, _href, _as } = target;

        return (
          <ElementLink id={_id} as={_as} href={_href} linkText={content} trackingId={`article_${_href}`}>
            {content}
          </ElementLink>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node) => {

        const target = get(node, 'data.target');
        const content = get(node, 'content[0].value');

        if (!content || !target || !target.url) return null;

        return (
          <ElementLink download target="_blank" as={target.url} href={target.url} linkText={content} trackingId={`article_${target.url}`}>
            {content}
          </ElementLink>
        );
      }
    }
  };
  return documentToReactComponents(document, options);
}

RichTextParser.propTypes = RichTextParserPropTypes;

export default RichTextParser;